import './ServiceStatusStyles.css';
import BlockUi from '@availity/block-ui';
import moment from 'moment';
import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import { Button, Form, Header, Icon, Label, Message, Statistic, Table } from 'semantic-ui-react';
import { getTaipJumpDiagnostics } from '../../actions/servicePageActions';
import { TaipJumpDiagnosticsVm, TaipJumpEventVm } from '../../types/servicePageTypes';
import { TaipJumpReportsDef, TaipJumpReportsHeader, toReportRow } from './csvReports';

const REFRESH_INTERVAL_MS = 10000;

const styles = {
    ipMismatchRow: {
        backgroundColor: '#fff6f6',
    } as React.CSSProperties,
    locationCell: {
        fontFamily: 'monospace',
        fontSize: '12px',
    } as React.CSSProperties,
};

const TaipJumpDiagnosticsPage: React.FC = () => {
    const [blockingState, setBlockingState] = useState(false);
    const [dataState, setDataState] = useState<TaipJumpDiagnosticsVm | undefined>(undefined);
    const [csvState, setCsvState] = useState<object[]>([]);
    const csvLinkRef = useRef<any>(null);

    const loadDiagnostics = useCallback(async (showBlocking = false) => {
        if (showBlocking) {
            setBlockingState(true);
        }

        try {
            const diagnostics = await getTaipJumpDiagnostics(200);
            setDataState(diagnostics);
        } catch {
            setDataState(undefined);
        } finally {
            if (showBlocking) {
                setBlockingState(false);
            }
        }
    }, []);

    useEffect(() => {
        loadDiagnostics(true);
        const intervalId = window.setInterval(() => loadDiagnostics(false), REFRESH_INTERVAL_MS);
        return () => window.clearInterval(intervalId);
    }, [loadDiagnostics]);

    const handleRefreshClick = async () => {
        await loadDiagnostics(true);
    };

    const handleCsvClick = () => {
        if (!dataState?.events?.length) {
            return;
        }

        const csvData = dataState.events.map(event => toReportRow(TaipJumpReportsDef, event));
        setCsvState(csvData);
        setTimeout(() => {
            csvLinkRef.current?.link.click();
        }, 0);
    };

    const formatLocation = (latitude: number, longitude: number) => `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;

    const renderRow = (event: TaipJumpEventVm) => (
        <Table.Row key={`${event.vehicleId}-${event.detectedAt}-${event.currentTimestamp}`} style={event.ipMismatch ? styles.ipMismatchRow : undefined}>
            <Table.Cell>{moment.parseZone(event.detectedAt).format('YYYY-MM-DD hh:mm A')}</Table.Cell>
            <Table.Cell>{event.vehicleId}</Table.Cell>
            <Table.Cell style={styles.locationCell}>{formatLocation(event.previousLatitude, event.previousLongitude)}</Table.Cell>
            <Table.Cell style={styles.locationCell}>{formatLocation(event.currentLatitude, event.currentLongitude)}</Table.Cell>
            <Table.Cell>{Math.round(event.distanceMeters)} m</Table.Cell>
            <Table.Cell>{Math.round(event.timeDifferenceSeconds)} s</Table.Cell>
            <Table.Cell>{event.previousSourceIp}</Table.Cell>
            <Table.Cell>
                {event.currentSourceIp}
                {event.ipMismatch && <Label color="red" size="mini" style={{ marginLeft: '0.5rem' }}>IP mismatch</Label>}
            </Table.Cell>
        </Table.Row>
    );

    return (
        <BlockUi tag="div" blocking={blockingState}>
            <Form>
                <Header as="h1" className="reportHeader">
                    TAIP Vehicle ID Jump Diagnostics
                    <Button basic color="grey" floated="right" onClick={handleRefreshClick}>
                        <Icon name="refresh" />
                        Refresh
                    </Button>
                </Header>

                <Form.Group>
                    <Form.Field width={16}>
                        <Message info>
                            <Message.Header>Temporary diagnostic tool</Message.Header>
                            <p>
                                Detects impossible TAIP movement for METRORail vehicles. Detection is based on distance and time thresholds.
                                Different source IPs are shown as supporting evidence only.
                            </p>
                            {dataState && (
                                <p>
                                    Last updated: {moment.parseZone(dataState.lastUpdated).format('YYYY-MM-DD hh:mm A')} (auto-refresh every 10 seconds)
                                </p>
                            )}
                        </Message>
                    </Form.Field>
                </Form.Group>

                {dataState && (
                    <>
                        <Form.Group widths="equal">
                            <Form.Field>
                                <Statistic>
                                    <Statistic.Value>{dataState.totalCount}</Statistic.Value>
                                    <Statistic.Label>Total jump events</Statistic.Label>
                                </Statistic>
                            </Form.Field>
                            <Form.Field>
                                <Statistic>
                                    <Statistic.Value>{dataState.ipMismatchCount}</Statistic.Value>
                                    <Statistic.Label>Events with IP mismatch</Statistic.Label>
                                </Statistic>
                            </Form.Field>
                            <Form.Field>
                                <Statistic>
                                    <Statistic.Value>{dataState.uniqueVehicleCount}</Statistic.Value>
                                    <Statistic.Label>Unique vehicles affected</Statistic.Label>
                                </Statistic>
                            </Form.Field>
                            <Form.Field>
                                <Button basic color="grey" onClick={handleCsvClick} disabled={!dataState.events.length}>
                                    <Icon name="download" />
                                    Download CSV
                                </Button>
                                <CSVLink
                                    headers={TaipJumpReportsHeader}
                                    data={csvState}
                                    filename={`TaipJumpDiagnostics_${moment(new Date()).format('YYYY_MM_DD_h_mm_A')}.csv`}
                                    className="hidden"
                                    ref={csvLinkRef}
                                    target="_blank"
                                />
                            </Form.Field>
                        </Form.Group>

                        <Table celled striped compact="very">
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell>Detection Time</Table.HeaderCell>
                                    <Table.HeaderCell>Vehicle ID</Table.HeaderCell>
                                    <Table.HeaderCell>Previous Location</Table.HeaderCell>
                                    <Table.HeaderCell>Current Location</Table.HeaderCell>
                                    <Table.HeaderCell>Distance</Table.HeaderCell>
                                    <Table.HeaderCell>Time Diff</Table.HeaderCell>
                                    <Table.HeaderCell>Previous IP</Table.HeaderCell>
                                    <Table.HeaderCell>Current IP</Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {dataState.events.length > 0
                                    ? dataState.events.map(renderRow)
                                    : (
                                        <Table.Row>
                                            <Table.Cell colSpan={8} textAlign="center">
                                                No jump events detected yet.
                                            </Table.Cell>
                                        </Table.Row>
                                    )}
                            </Table.Body>
                        </Table>
                    </>
                )}
            </Form>
        </BlockUi>
    );
};

export default TaipJumpDiagnosticsPage;
